import { useMemo } from 'react';

import { useSelector } from '../providers/StoreProvider';
import { type Bet } from '../types';

export const useBets = (): Bet[] => {
  const { betIdsToShow, bets, bookmakers } = useSelector(store => ({
    betIdsToShow: store.betIdsToShow,
    bets: store.bets,
    bookmakers: store.bookmakers,
  }));

  return useMemo(() => {
    // Display is gated on the bookies the API actually returned for this response
    // (the backend is repub-aware and already applies overrideBookies). The grid
    // response's bookmakers array is the single source of truth.
    const returnedBookieCodes = new Set((bookmakers ?? []).map(bookmaker => bookmaker.code));
    const filterOdds = (bet: Bet): Bet => ({
      ...bet,
      odds: bet.odds.filter(odd => returnedBookieCodes.has(odd.bookmakerCode)),
    });

    // If no betIdsToShow specified, use all bets
    if (!betIdsToShow || betIdsToShow.length === 0) {
      return bets.map(filterOdds);
    }

    // Filter bets by the specified IDs
    const filteredBets = bets.filter(bet => betIdsToShow.includes(bet.id));

    // If filtering would result in no bets, fallback to all bets
    const betsToProcess = filteredBets.length > 0 ? filteredBets : bets;

    return betsToProcess.map(filterOdds);
  }, [bets, betIdsToShow, bookmakers]);
};
